import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { createServiceOrder, uploadOrderPhotos } from './os.api';
import { searchCustomers, createCustomer } from '../pos/pos.api';
import { api, readApiError } from '../../lib/api';
import type { CustomerDTO } from '@horology/shared-types';

interface ServiceTemplateDTO {
  id: number;
  name: string;
  reportedDefects: string;
  worksToPerform: string;
  estimatedValue: string;
  promiseDays: number | null;
}

export function NewOrderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'customer' | 'watch' | 'photos'>('customer');

  const [customer, setCustomer] = useState<CustomerDTO | null>(null);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerForm, setCustomerForm] = useState({
    idDocument: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [watch, setWatch] = useState({
    brand: '',
    model: '',
    serial: '',
    watchKind: '',
    caseMaterial: '',
    bandMaterial: '',
    reportedDefects: '',
    worksToPerform: '',
    estimatedValue: '',
    promiseAt: '',
  });

  const [photos, setPhotos] = useState<(File | null)[]>([null, null, null, null]);
  const [error, setError] = useState<string | null>(null);

  const search = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn: () => searchCustomers(customerSearch),
    enabled: customerSearch.length >= 2 && !creatingCustomer,
  });

  const createCustomerMut = useMutation({
    mutationFn: createCustomer,
    onSuccess: (c) => {
      setCustomer(c);
      setCreatingCustomer(false);
      setStep('watch');
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!customer) throw new Error('Cliente requerido');
      const order = await createServiceOrder({
        customerId: customer.id,
        brand: watch.brand,
        model: watch.model,
        serial: watch.serial || undefined,
        watchKind: watch.watchKind || undefined,
        caseMaterial: watch.caseMaterial || undefined,
        bandMaterial: watch.bandMaterial || undefined,
        reportedDefects: watch.reportedDefects,
        worksToPerform: watch.worksToPerform,
        estimatedValue: Number(watch.estimatedValue),
        promiseAt: watch.promiseAt ? new Date(watch.promiseAt).toISOString() : undefined,
      });

      const filesToUpload = photos.filter((f): f is File => f !== null);
      if (filesToUpload.length > 0) {
        await uploadOrderPhotos(order.id, filesToUpload);
      }
      return order;
    },
    onSuccess: (order) => {
      void navigate({ to: '/orders/$id', params: { id: String(order.id) } });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const photoCount = photos.filter((f) => f !== null).length;
  const canSubmit =
    customer &&
    watch.brand.trim() &&
    watch.model.trim() &&
    watch.reportedDefects.trim() &&
    watch.worksToPerform.trim() &&
    Number(watch.estimatedValue) >= 0 &&
    photoCount === 4;

  return (
    <section style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Nueva Orden de Servicio</h1>
        <button className="btn" onClick={() => navigate({ to: '/orders' })}>← Volver</button>
      </div>

      <ol style={{ display: 'flex', listStyle: 'none', padding: 0, gap: '0.5rem', marginBottom: '1.5rem' }}>
        {[
          { key: 'customer', label: '1. Cliente' },
          { key: 'watch', label: '2. Reloj y trabajos' },
          { key: 'photos', label: '3. Fotos (4)' },
        ].map((s) => (
          <li
            key={s.key}
            onClick={() => setStep(s.key as typeof step)}
            style={{
              padding: '0.4rem 1rem',
              borderRadius: 999,
              cursor: 'pointer',
              background: step === s.key ? 'var(--color-primary)' : 'var(--color-surface)',
              color: step === s.key ? '#fff' : 'var(--color-text)',
              border: '1px solid var(--color-border)',
              fontSize: '0.85rem',
            }}
          >
            {s.label}
          </li>
        ))}
      </ol>

      {error && <p className="error">{error}</p>}

      {step === 'customer' && (
        <div className="card">
          {!creatingCustomer ? (
            <>
              <h3 style={{ marginTop: 0 }}>Buscar cliente</h3>
              <input
                className="input"
                placeholder="Cédula, nombre o teléfono..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                autoFocus
              />
              <div style={{ marginTop: '0.5rem', maxHeight: 280, overflowY: 'auto' }}>
                {search.data?.map((c) => (
                  <button
                    key={c.id}
                    className="btn"
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '0.25rem',
                      ...(customer?.id === c.id && {
                        borderColor: 'var(--color-primary)',
                        background: 'var(--color-bg)',
                      }),
                    }}
                    onClick={() => {
                      setCustomer(c);
                      setStep('watch');
                    }}
                  >
                    {c.firstName} {c.lastName} — <small>{c.idDocument} · {c.phone}</small>
                  </button>
                ))}
                {search.data && search.data.length === 0 && customerSearch.length >= 2 && (
                  <p style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>
                    Sin resultados.
                  </p>
                )}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button className="btn btn-primary" onClick={() => setCreatingCustomer(true)}>
                  + Cliente nuevo
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Nuevo cliente</h3>
              <div className="field">
                <label>Cédula</label>
                <input
                  className="input"
                  value={customerForm.idDocument}
                  onChange={(e) => setCustomerForm({ ...customerForm, idDocument: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div className="field">
                  <label>Nombre</label>
                  <input
                    className="input"
                    value={customerForm.firstName}
                    onChange={(e) => setCustomerForm({ ...customerForm, firstName: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Apellido</label>
                  <input
                    className="input"
                    value={customerForm.lastName}
                    onChange={(e) => setCustomerForm({ ...customerForm, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="field">
                <label>Email</label>
                <input
                  className="input"
                  type="email"
                  value={customerForm.email}
                  onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Teléfono</label>
                <input
                  className="input"
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-primary"
                  disabled={createCustomerMut.isPending}
                  onClick={() => createCustomerMut.mutate(customerForm)}
                >
                  {createCustomerMut.isPending ? 'Creando...' : 'Crear y continuar'}
                </button>
                <button className="btn" onClick={() => setCreatingCustomer(false)}>
                  Volver a búsqueda
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'watch' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Datos del reloj y presupuesto</h3>
          {customer && (
            <p style={{ color: 'var(--color-muted)', marginTop: 0 }}>
              Cliente: <strong>{customer.firstName} {customer.lastName}</strong> ({customer.idDocument})
            </p>
          )}

          <TemplateChips
            onPick={(t) => {
              const promiseDate = t.promiseDays
                ? new Date(Date.now() + t.promiseDays * 86_400_000).toISOString().slice(0, 16)
                : watch.promiseAt;
              setWatch({
                ...watch,
                reportedDefects: t.reportedDefects,
                worksToPerform: t.worksToPerform,
                estimatedValue: String(t.estimatedValue),
                promiseAt: promiseDate,
              });
            }}
          />


          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="field">
              <label>Marca *</label>
              <input className="input" value={watch.brand} onChange={(e) => setWatch({ ...watch, brand: e.target.value })} />
            </div>
            <div className="field">
              <label>Modelo *</label>
              <input className="input" value={watch.model} onChange={(e) => setWatch({ ...watch, model: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
            <div className="field">
              <label>Serial</label>
              <input className="input" value={watch.serial} onChange={(e) => setWatch({ ...watch, serial: e.target.value })} />
            </div>
            <div className="field">
              <label>Tipo</label>
              <select className="input" value={watch.watchKind} onChange={(e) => setWatch({ ...watch, watchKind: e.target.value })}>
                <option value="">—</option>
                <option value="Mecánico">Mecánico</option>
                <option value="Automático">Automático</option>
                <option value="Cuarzo">Cuarzo</option>
                <option value="Smart">Smart</option>
              </select>
            </div>
            <div className="field">
              <label>Material caja</label>
              <input className="input" value={watch.caseMaterial} onChange={(e) => setWatch({ ...watch, caseMaterial: e.target.value })} />
            </div>
          </div>
          <div className="field">
            <label>Material pulso/correa</label>
            <input className="input" value={watch.bandMaterial} onChange={(e) => setWatch({ ...watch, bandMaterial: e.target.value })} />
          </div>
          <div className="field">
            <label>Defectos reportados *</label>
            <textarea
              className="input"
              rows={3}
              value={watch.reportedDefects}
              onChange={(e) => setWatch({ ...watch, reportedDefects: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Trabajos a realizar *</label>
            <textarea
              className="input"
              rows={3}
              value={watch.worksToPerform}
              onChange={(e) => setWatch({ ...watch, worksToPerform: e.target.value })}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="field">
              <label>Valor estimado *</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={watch.estimatedValue}
                onChange={(e) => setWatch({ ...watch, estimatedValue: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Fecha promesa</label>
              <input
                className="input"
                type="datetime-local"
                value={watch.promiseAt}
                onChange={(e) => setWatch({ ...watch, promiseAt: e.target.value })}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn btn-primary" onClick={() => setStep('photos')}>
              Continuar a fotos →
            </button>
            <button className="btn" onClick={() => setStep('customer')}>
              ← Volver
            </button>
          </div>
        </div>
      )}

      {step === 'photos' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Fotos del reloj</h3>
          <p style={{ color: 'var(--color-muted)' }}>
            Adjunta exactamente 4 fotos (frente, dorso, defecto principal, accesorios).
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
            {[0, 1, 2, 3].map((i) => (
              <PhotoSlot
                key={i}
                index={i}
                file={photos[i] ?? null}
                onChange={(f) => setPhotos((prev) => prev.map((x, j) => (j === i ? f : x)))}
              />
            ))}
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: photoCount === 4 ? 'var(--color-accent)' : 'var(--color-muted)' }}>
            {photoCount} de 4 fotos cargadas.
          </p>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-primary"
              disabled={!canSubmit || submitMut.isPending}
              onClick={() => submitMut.mutate()}
            >
              {submitMut.isPending ? 'Creando OS...' : 'Crear orden de servicio'}
            </button>
            <button className="btn" onClick={() => setStep('watch')}>← Volver</button>
          </div>
        </div>
      )}
    </section>
  );
}

function PhotoSlot(props: {
  index: number;
  file: File | null;
  onChange: (f: File | null) => void;
}) {
  const url = props.file ? URL.createObjectURL(props.file) : null;
  return (
    <label
      style={{
        aspectRatio: '1 / 1',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--radius)',
        backgroundImage: url ? `url(${url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
        color: 'var(--color-muted)',
        fontSize: '0.85rem',
        position: 'relative',
      }}
    >
      <input
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => props.onChange(e.target.files?.[0] ?? null)}
      />
      {!url && <span>Foto {props.index + 1}</span>}
      {url && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            props.onChange(null);
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: 'rgba(0,0,0,0.5)',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '2px 6px',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      )}
    </label>
  );
}

function TemplateChips({ onPick }: { onPick: (t: ServiceTemplateDTO) => void }) {
  const templates = useQuery({
    queryKey: ['os-templates'],
    queryFn: async () => (await api.get<ServiceTemplateDTO[]>('/api/os-templates')).data,
  });

  if (!templates.data || templates.data.length === 0) return null;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div
        style={{
          fontSize: '0.75rem',
          color: 'var(--color-muted)',
          textTransform: 'uppercase',
          marginBottom: '0.35rem',
        }}
      >
        Plantillas rápidas
      </div>
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        {templates.data.map((t) => (
          <button
            key={t.id}
            type="button"
            className="btn"
            onClick={() => onPick(t)}
            style={{ padding: '4px 10px', fontSize: '0.85rem' }}
          >
            {t.name}
          </button>
        ))}
      </div>
    </div>
  );
}
